import { useEffect, useState } from "react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Plus,
  Loader2,
  MessageSquare,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../components/ui/dropdown-menu";
import {
  createTicket,
  getMyTickets,
  deleteMyTicket,
  reopenTicket,
  TicketType,
  TicketStatus,
  TICKET_SUBTYPES_BY_TYPE,
  getStatusLabel,
  getStatusColor,
  getTypeLabel,
  getSubtypeLabel,
  formatDate,
  getRelativeTime,
  validateCreateTicket,
} from "../../api/ticketApi";
import { useSnapshot } from "valtio";
import { authStore } from "@/stores/authStore";
import { permissionStore, PermissionAction, ResourceType } from "@/stores/permissionStore";

// Category mapping for UI (Billing removed from create form)
const CATEGORY_OPTIONS = [
  { value: TicketType.TECHNICAL, label: "Technical Issue" },
  { value: TicketType.ACCOUNT, label: "Account Issue" },
  { value: TicketType.GENERAL, label: "General Inquiry" },
];

const SUBCATEGORY_MAP = TICKET_SUBTYPES_BY_TYPE;

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [ticketCounts, setTicketCounts] = useState({
    [TicketStatus.PENDING]: 0,
    [TicketStatus.IN_PROGRESS]: 0,
    [TicketStatus.RESOLVED]: 0,
    [TicketStatus.CLOSED]: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [openTicket, setOpenTicket] = useState(null);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [submittingTicket, setSubmittingTicket] = useState(false);

  // Form state
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketCategory, setTicketCategory] = useState("");
  const [ticketSubcategory, setTicketSubcategory] = useState("");
  const [ticketDesc, setTicketDesc] = useState("");
  const [ticketExtraInfo, setTicketExtraInfo] = useState("");
  const [ticketError, setTicketError] = useState("");
  const snap = useSnapshot(authStore);
  const permSnap = useSnapshot(permissionStore);
  const user = snap.user;
  const pageSize = 4;
  
  // Permission checks
  const canCreateTicket = permSnap.can(PermissionAction.CREATE, ResourceType.SUPPORT_TICKET);

  const getInitials = (name) => {
    if (!name) return "";
    const words = name.trim().split(" ");
    if (words.length === 1) return words[0][0].toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  // Fetch tickets
  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getMyTickets();
      setTickets(result.tickets);
      setTicketCounts(result.counts);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to fetch tickets"
      );
    } finally {
      setLoading(false);
    }
  };

  // Filter and search
  const filtered = tickets.filter(
    (t) =>
      (filter === "All" || t.status === filter) &&
      (t.title?.toLowerCase().includes(search.toLowerCase()) ||
        getTypeLabel(t.type)?.toLowerCase().includes(search.toLowerCase()) ||
        t.description?.toLowerCase().includes(search.toLowerCase()))
  );
  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Reset form
  const resetForm = () => {
    setTicketSubject("");
    setTicketCategory("");
    setTicketSubcategory("");
    setTicketDesc("");
    setTicketExtraInfo("");
    setTicketError("");
  };

  // Handle ticket submission
  const handleSubmitTicket = async () => {
    if (!canCreateTicket) {
      setTicketError("You don't have permission to create tickets");
      return;
    }
    
    if (!ticketCategory || !ticketSubcategory) {
      setTicketError("Please select both category and subcategory.");
      return;
    }

    const ticketData = {
      title: ticketSubject.trim(),
      description: ticketDesc.trim(),
      type: ticketCategory,
      subtype: ticketSubcategory,
      extraInfo: ticketExtraInfo.trim() || undefined,
    };

    const validationErrors = validateCreateTicket(ticketData);
    if (validationErrors.length > 0) {
      setTicketError(validationErrors.join(", "));
      return;
    }

    setSubmittingTicket(true);
    setTicketError("");

    try {
      await createTicket(ticketData);
      setShowTicketForm(false);
      resetForm();
      await fetchTickets(); // Refresh tickets list
    } catch (err) {
      setTicketError(
        err.response?.data?.message || err.message || "Failed to create ticket"
      );
    } finally {
      setSubmittingTicket(false);
    }
  };

  // Handle ticket deletion
  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm("Are you sure you want to delete this ticket?")) {
      return;
    }

    try {
      await deleteMyTicket(ticketId);
      await fetchTickets(); // Refresh tickets list
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to delete ticket"
      );
    }
  };

  // Handle ticket reopening
  const handleReopenTicket = async (ticketId) => {
    if (!window.confirm("Are you sure you want to reopen this ticket?")) {
      return;
    }

    try {
      await reopenTicket(ticketId);
      await fetchTickets(); // Refresh tickets list
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to reopen ticket"
      );
    }
  };

  // Get subcategory options based on selected category
  const getSubcategoryOptions = () => {
    if (!ticketCategory) return [];
    return SUBCATEGORY_MAP[ticketCategory] || [];
  };

  return (
    <div className="min-h-[80vh] w-full py-6 px-2">
      {/* Modal for Create New Ticket */}
      {showTicketForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 py-4 relative border border-[#e3eaf3] max-h-[90vh] overflow-y-auto my-auto">
            {/* Sticky Header: Title + Close Button */}
            <div className="sticky top-0 z-10 -mx-6 px-6 pt-2 pb-4 mb-3 bg-white flex items-center justify-between border-b border-[#e3eaf3]">
              <div className="text-2xl font-bold text-[#357389]">
                Create New Ticket
              </div>
              <button
                className="text-xl text-gray-600 hover:text-gray-800"
                onClick={() => {
                  setShowTicketForm(false);
                  resetForm();
                }}
                aria-label="Close"
                disabled={submittingTicket}
              >
                &times;
              </button>
            </div>

            {/* Subject */}
            <div className="mb-2">
              <label className="block text-base font-medium text-gray-700 mb-1">
                Subject of the Ticket <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full px-4 py-3 rounded-lg border border-[#e3eaf3] bg-[#f8fafc] text-base outline-none placeholder-gray-400 disabled:opacity-50"
                placeholder="Enter name..."
                value={ticketSubject}
                onChange={(e) => setTicketSubject(e.target.value)}
                maxLength={100}
                disabled={submittingTicket}
              />
              <div className="text-xs text-gray-500 mt-1">
                {ticketSubject.length}/100 characters
              </div>
            </div>

            {/* Category */}
            <div className="mb-2">
              <label className="block text-base font-medium text-gray-700 mb-2">
                Category of ticket <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {CATEGORY_OPTIONS.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => {
                      setTicketCategory(cat.value);
                      setTicketSubcategory(""); // reset subcategory
                    }}
                    className={`px-4 py-2 rounded-full border ${
                      ticketCategory === cat.value
                        ? "bg-[#357389] text-white"
                        : "bg-[#e3eaf3] text-[#357389] hover:bg-[#d0e4ef]"
                    }`}
                    disabled={submittingTicket}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Subcategory */}
            {ticketCategory && (
              <div className="mb-2">
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Sub-category <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {getSubcategoryOptions(ticketCategory).map((subcat) => (
                    <button
                      key={subcat.value}
                      type="button"
                      onClick={() => setTicketSubcategory(subcat.value)}
                      className={`px-4 py-2 rounded-full border ${
                        ticketSubcategory === subcat.value
                          ? "bg-[#357389] text-white"
                          : "bg-[#e3eaf3] text-[#357389] hover:bg-[#d0e4ef]"
                      }`}
                      disabled={submittingTicket}
                    >
                      {subcat.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            <div className="mb-4">
              <label className="block text-base font-medium text-gray-700 mb-2">
                Ticket description <span className="text-red-500">*</span>
              </label>
              <textarea
                className="w-full px-4 py-3 rounded-lg border border-[#e3eaf3] bg-[#f8fafc] text-base outline-none placeholder-gray-400 min-h-[120px] resize-none disabled:opacity-50"
                placeholder="Describe your complaint or issue here..."
                value={ticketDesc}
                onChange={(e) => setTicketDesc(e.target.value)}
                maxLength={1000}
                disabled={submittingTicket}
              />
              <div className="text-xs text-gray-500 mt-1">
                {ticketDesc.length}/1000 characters
              </div>
            </div>

            {/* Error */}
            {ticketError && (
              <div className="text-xs text-red-500 mb-3">{ticketError}</div>
            )}

            {/* Buttons */}
            <div className="flex justify-between">
              <button
                type="button"
                className="px-8 py-2 rounded-full bg-white border border-[#e3eaf3] text-[#357389] font-semibold hover:bg-[#f8fafc] transition disabled:opacity-50"
                onClick={() => {
                  setShowTicketForm(false);
                  resetForm();
                }}
                disabled={submittingTicket}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-10 py-2 rounded-full bg-[#6ea6b9] text-white font-semibold hover:bg-[#357389] transition flex items-center gap-2 disabled:opacity-50"
                onClick={handleSubmitTicket}
                disabled={submittingTicket}
              >
                {submittingTicket && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
                {submittingTicket ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="w-full relative mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex gap-2 flex-1">
            <Input
              placeholder="Search for tickets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full max-w-[320px]"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2">
                  Filter: {filter === "All" ? "All" : getStatusLabel(filter)}
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setFilter("All")}>
                  All ({tickets.length})
                </DropdownMenuItem>
                {Object.entries(ticketCounts).map(([status, count]) => (
                  <DropdownMenuItem
                    key={status}
                    onClick={() => setFilter(status)}
                  >
                    {getStatusLabel(status)} ({count})
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Button
            className="flex items-center gap-2 bg-[var(--primary-font)] hover:bg-[var(--secondary-font)] text-white px-4 py-2 rounded-lg shadow-xs"
            onClick={() => {
              if (!canCreateTicket) {
                setTicketError("You don't have permission to create tickets");
                return;
              }
              setShowTicketForm(true);
            }}
            disabled={!canCreateTicket}
          >
            <Plus className="w-5 h-5" />
            New Ticket
          </Button>
        </div>

        {/* Tickets List */}
        <div className="flex flex-col gap-4 items-start justify-start w-full relative">
          {loading ? (
            <div className="flex flex-col gap-4 p-8 w-full">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="h-[70px] bg-[#e3eaf3] rounded-xl animate-pulse"
                />
              ))}
            </div>
          ) : error ? (
            <div className="text-[#d9534f] text-center py-10 w-full">
              {error}
              <Button variant="outline" className="ml-4" onClick={fetchTickets}>
                Retry
              </Button>
            </div>
          ) : paginated.length === 0 ? (
            <div className="text-[#7a869a] text-center py-10 w-full">
              {search ? "No tickets match your search." : "No tickets found."}
            </div>
          ) : (
            paginated.map((ticket) => {
              const expanded = openTicket === ticket.id;
              return (
                <div
                  key={ticket.id}
                  className={`bg-white w-full rounded-xl border border-gray-200 transition-all duration-300 hover:shadow-md ${
                    expanded
                      ? "shadow-lg ring-2 ring-blue-100 border-blue-200"
                      : ""
                  }`}
                >
                  {/* Main Ticket Header */}
                  <div
                    className="flex items-start gap-4 px-6 py-5 cursor-pointer select-none"
                    onClick={() => setOpenTicket(expanded ? null : ticket.id)}
                  >
                    {/* Main Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between w-full mb-2">
                        <div className="flex items-center gap-1">
                          <span className="w-5 h-5 rounded-full bg-[#5D93A5] text-white flex items-center text-[10px] justify-center mr-2">
                            {getInitials(user?.name || "User")}
                          </span>

                          <span className="text-sm text-gray-500 font-medium">
                            You
                          </span>
                          <span className="text-sm text-gray-400">•</span>
                          <span className="text-sm text-gray-500">
                            {getRelativeTime(ticket.createdAt)}
                          </span>
                        </div>
                        <div className="flex gap-3 items-center">
                          {ticket.statusUpdate && (
                            <Badge
                              variant="outline"
                              className="rounded-full px-3 text-xs text-green-700 border-green-500"
                            >
                              Reply Received
                            </Badge>
                          )}
                          <Badge variant="blue" className="rounded-full px-4">
                            {getSubtypeLabel(ticket.subtype)}
                          </Badge>

                          <div className="relative">
                            <Badge
                              style={{
                                backgroundColor: getStatusColor(ticket.status),
                                color: "white",
                              }}
                              className="rounded-full px-4"
                            >
                              {getStatusLabel(ticket.status)}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      <div className="font-semibold text-base mb-1 flex items-center gap-2">
                        <img
                          src="/label_important.svg"
                          alt="User Avatar"
                          className="w-5 h-5 rounded-full"
                        />
                        {ticket.title}
                      </div>

                      <div
                        className={`text-gray-600 text-sm mb-2 ${expanded ? "" : "line-clamp-2"}`}
                      >
                        {expanded
                          ? ticket.description || "No description provided."
                          : ticket.description || "No description provided."}
                      </div>
                    </div>

                    {/* Expand/Collapse Icon */}
                    <ChevronDown
                      className={`w-5 h-5 text-gray-400 mt-1 ml-2 transition-transform duration-200 ${
                        expanded ? "rotate-180" : ""
                      }`}
                    />
                  </div>

                  {/* Expanded Details */}
                  {expanded && (
                    <div className="px-6 pb-6 border-t border-gray-100">
                      <div className="pt-6">
                        {/* Detailed Information Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-1">
                              Created
                            </h4>
                            <p className="text-sm text-gray-600">
                              {formatDate(ticket.createdAt)}
                            </p>
                          </div>

                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-1">
                              Last Updated
                            </h4>
                            <p className="text-sm text-gray-600">
                              {formatDate(ticket.updatedAt)}
                            </p>
                          </div>

                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-1">
                              Category
                            </h4>
                            <p className="text-sm text-gray-600">
                              {getTypeLabel(ticket.type)}
                            </p>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-1">
                              Sub Category
                            </h4>
                            <p className="text-sm text-gray-600">
                              {getSubtypeLabel(ticket.subtype)}
                            </p>
                          </div>

                          {/* <div className="space-y-4">
                            {ticket.resolvedAt && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-900 mb-1">
                                  Resolved
                                </h4>
                                <p className="text-sm text-gray-600">
                                  {formatDate(ticket.resolvedAt)}
                                </p>
                              </div>
                            )}
                          </div> */}
                        </div>
                        {/* Status Update */}

                        {ticket.statusUpdate && (
                          <div className="border-l-4 border-blue-500 p-4 rounded-r-lg">
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <MessageSquare className="h-4 w-4 text-blue-600" />
                              </div>
                              <div className="flex-1 space-y-2">
                                <div className="flex items-c  enter justify-between">
                                  <h4 className="text-sm font-semibold text-gray-900">
                                    Status Update
                                  </h4>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">
                                      {getRelativeTime(ticket.updatedAt)}
                                    </span>
                                  </div>
                                </div>
                                <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                                  <p className="text-sm text-gray-800 leading-relaxed">
                                    {ticket.statusUpdate}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        {ticket.status === TicketStatus.RESOLVED && (
                          <div className="flex gap-3 pt-4 border-t border-gray-100 mt-4">
                            <Button
                              className="bg-[#6ea6b9] text-white hover:bg-[#357389] flex items-center gap-2"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReopenTicket(ticket.id);
                              }}
                            >
                              Reopen Ticket
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-6 text-[#7a869a] text-sm">
          <div>
            Rows per page: <span className="font-semibold">{pageSize}</span>
          </div>
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="px-2"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="mx-2">
              {filtered.length === 0 ? 0 : pageSize * (page - 1) + 1}-
              {Math.min(page * pageSize, filtered.length)} of {filtered.length}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="px-2"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || totalPages === 0}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
